import { css } from "@emotion/react";
import React, { useRef } from "react";
import { Icon } from "../Icon";
import { Paper } from "../Paper";
import { Portal, PortalProps } from "../Portal";
import { resetButtonStyle } from "../Button";
import { useOnClickOutside } from "../hooks/useOnClickOutside";
import { useTheme } from "../theme";
import { ModalBackdrop } from "./ModalBackdrop";

export interface ModalProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    Pick<PortalProps, "disablePortal"> {
  /**
   * ModalのModalHeaderの右側のXボタンを押すと発火します。
   *
   * このPropを指定しない場合、ModalHeaderの右側のXボタンは表示されません。
   *
   */
  onClose?: () => void;
  /**
   * falseを指定した場合、Modalは表示されません。
   *
   * @default true
   */
  open?: boolean;
  /**
   * Modalの外側をクリックした場合、発火します。
   */
  onClickOutside?: (event: MouseEvent | TouchEvent) => void;
  /**
   * 全画面表示モードになります。
   */
  backdrop?: boolean;
  /**
   * Modal上部に指定された要素を挿入します。
   */
  header?: React.JSX.Element;
  /**
   * Modalに指定された要素を挿入します。
   */
  body?: React.JSX.Element;
  /**
   * Modal下部に指定された要素を挿入します。
   */
  footer?: React.JSX.Element;
  /**
   * Modalの幅を固定にしたい場合に指定します。
   * maxWidthを超えられない点には注意してください。
   * @deprecated 代わりにsizeを利用してください。
   * @default undefined
   */
  width?: string | number;
  /**
   * Modalの最大幅を指定します。
   * @deprecated 代わりにsizeを利用してください。
   */
  maxWidth?: string | number;
  /**
   * Modalの大きさを指定します。
   *
   * - sm: width: 100%; max-width: 400px;
   * - md: width: 100%; max-width: 500px;
   * - lg: width: 100%; min-width: 800px;
   *
   * widthやmaxWidthが指定されている場合、そちらが優先されます。
   * @default "md"
   */
  size?: "sm" | "md" | "lg";
}

interface ContainerProps {
  backdrop?: boolean;
  children?: React.ReactNode;
}

const sizeStyles: Record<
  NonNullable<ModalProps["size"]>,
  React.CSSProperties
> = {
  sm: {
    maxWidth: 400,
    width: "100%",
  },
  md: {
    maxWidth: 500,
    width: "100%",
  },
  lg: {
    minWidth: 800,
    width: "100%",
  },
};

const Container = ({ backdrop, children }: ContainerProps) => {
  return backdrop ? (
    <ModalBackdrop>{children}</ModalBackdrop>
  ) : (
    <React.Fragment>{children}</React.Fragment>
  );
};

export const Modal = (props: ModalProps) => {
  const {
    open = true,
    header,
    body,
    footer,
    style,
    className,
    onClickOutside,
    backdrop,
    width,
    maxWidth,
    size = "md",
    onClose,
    disablePortal,
  } = props;
  const ref = useRef(null);
  useOnClickOutside(ref, onClickOutside);

  const theme = useTheme();

  const sizeStyle = sizeStyles[size];

  return open ? (
    <Portal disablePortal={disablePortal}>
      <Container backdrop={backdrop}>
        <Paper
          ref={ref}
          style={{
            ...style,
            // width/maxWidthが明示指定された場合はsizeのminWidth制約を適用しない
            ...(sizeStyle.minWidth !== undefined &&
            width === undefined &&
            maxWidth === undefined
              ? { minWidth: sizeStyle.minWidth }
              : null),
            width: width ?? sizeStyle.width,
            maxWidth: maxWidth ?? sizeStyle.maxWidth,
          }}
          className={className}
          css={[
            css`
              z-index: ${theme.zIndex.modal};
              display: flex;
              flex-direction: column;
              border: 1px solid ${theme.palette.gray[300]};
              border-radius: 4px;
            `,
            backdrop
              ? css`
                  margin: 0px auto;
                `
              : null,
          ]}
        >
          {header ? (
            <header
              css={css`
                display: grid;
                grid-template-columns: 1fr auto;
                align-items: center;
                padding: ${theme.spacing * 3}px ${theme.spacing * 4}px;
                background-color: ${theme.palette.gray[100]};
                border-bottom: 1px solid ${theme.palette.gray[300]};
                border-top-left-radius: 0.3em;
                border-top-right-radius: 0.3em;
              `}
              ref={ref}
            >
              <div
                css={css`
                  grid-row: 1;
                  grid-column: 1/3;
                `}
              >
                {header}
              </div>
              {onClose && (
                <button
                  css={[
                    resetButtonStyle,
                    css`
                      z-index: 1;
                      grid-row: 1;
                      grid-column: 2;
                      padding: ${theme.spacing}px ${theme.spacing * 2}px;
                      color: ${theme.palette.gray[400]};
                      transition: 0.3s;
                      &:hover {
                        color: ${theme.palette.gray[500]};
                      }
                    `,
                  ]}
                  onClick={onClose}
                >
                  <Icon.Close />
                </button>
              )}
            </header>
          ) : null}
          <div
            css={css`
              flex: 1;
              padding: ${theme.spacing * 3}px ${theme.spacing * 4}px;
              overflow-y: auto;
            `}
          >
            {body}
          </div>
          {footer ? (
            <footer
              css={css`
                padding: ${theme.spacing * 2}px ${theme.spacing * 4}px;
                border-top: 1px solid ${theme.palette.gray[300]};
                border-bottom-right-radius: 0.3em;
                border-bottom-left-radius: 0.3em;
              `}
            >
              {footer}
            </footer>
          ) : null}
        </Paper>
      </Container>
    </Portal>
  ) : null;
};
